import React, { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

const resourceOptions = [
  "Accounting",
  "Business Plan",
  "Buy / Sell a Business",
  "Cash Flow Management",
  "Company Formation",
  "Customer Relations",
  "Credit Counseling",
  "Financing / Capital",
  "Franchising",
  "Government Contracting",
  "Human Resources and Management",
  "International Trade",
  "Legal Issues",
  "Marketing / Sales",
  "Private Sector Procurement",
  "Tax Planning",
  "Technology and E-Commerce",
  "None"
];

const ResourcesStep = ({ onNext, initialValue = [], isSubmitting = false }) => {
  const [selectedResources, setSelectedResources] = useState(initialValue);

  const handleResourceChange = (value) => {
    if (value === "None") {
      // If "None" is selected, clear all other selections
      setSelectedResources(["None"]);
    } else {
      setSelectedResources(prev => {
        const newSelection = prev.includes(value)
          ? prev.filter(item => item !== value)
          : [...prev.filter(item => item !== "None"), value];
        return newSelection.length === 0 ? [] : newSelection;
      });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="flex flex-col space-y-6"
    >
      <div className="text-center space-y-2">
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-2xl md:text-3xl font-bold text-gray-900"
        >
          Are you interested in additional support or resources for your small business?
        </motion.h2>
        <motion.div 
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="w-12 h-0.5 bg-[#1a4a1a] mx-auto"
        />
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-gray-600 text-sm md:text-base max-w-2xl mx-auto"
        >
          If so- please select any/all you're interested in and we will work to match you to a resource partner in addition to a capital partner. If not, please select None.
        </motion.p>
      </div>

      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-3xl mx-auto w-full"
      >
        {resourceOptions.map((option, index) => (
          <motion.div
            key={option}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ 
              delay: 0.1 * index,
              type: "spring",
              stiffness: 100,
              damping: 10
            }}
            className={`
              flex items-center space-x-3 rounded-lg border border-slate-200 p-4 
              hover:border-[#1a4a1a]/20 hover:bg-slate-50 transition-all duration-200
              ${selectedResources.includes(option) ? 'border-[#1a4a1a] bg-slate-50 shadow-sm' : ''}
              ${option === "None" ? 'md:col-span-2 bg-gray-50' : ''}
            `}
          >
            <Checkbox
              id={option}
              checked={selectedResources.includes(option)}
              onCheckedChange={() => handleResourceChange(option)}
              disabled={isSubmitting}
              className="data-[state=checked]:border-[#1a4a1a] data-[state=checked]:bg-[#1a4a1a]"
            />
            <Label
              htmlFor={option}
              className="text-base cursor-pointer flex-grow font-medium text-gray-700"
            >
              {option}
            </Label>
          </motion.div>
        ))}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="mt-auto pt-6"
      >
        <Button
          onClick={() => onNext(selectedResources)}
          disabled={selectedResources.length === 0 || isSubmitting}
          className="w-full bg-[#1a4a1a] hover:bg-[#1a4a1a]/90 text-white py-6 text-lg font-semibold rounded-lg shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <div className="flex items-center justify-center space-x-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Submitting...</span>
            </div>
          ) : (
            "Continue"
          )}
        </Button>
      </motion.div>
    </motion.div>
  );
};

export default ResourcesStep; 